import { toTitleCase } from 'features/dynamicOntology'
import { unwrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import type {
  FieldValues,
  OntologyField,
} from 'features/libra/ontology/ontologyFieldTypes'
import { formatReportFieldValue } from 'features/libra/pdf/reportFieldFormatting'
import {
  niiScreeningConfigSchema,
  type NiiScreeningTicketProps,
} from 'features/libra/stepComponents/niiScreening/niiScreeningApi'
import type { LibraWorkflowStep } from 'features/libra/workflow/workflowStepsApi'

import {
  buildNiiFormSectionBlocks,
  type NiiFormSectionBlock,
} from './buildNiiFormSectionBlocks'

interface LegacyScreeningRow {
  key: keyof NiiScreeningTicketProps
  label: string
  /** Free-text fields are exported verbatim; the rest are enum/boolean codes. */
  freeText?: boolean
}

const LEGACY_SUITABILITY_ROWS: ReadonlyArray<LegacyScreeningRow> = [
  {
    key: 'isIntrinsicallySuitable',
    label: 'Is the vessel intrinsically suitable?',
  },
  {
    key: 'previousInspectionHistoryValidity',
    label: 'Is previous inspection history still valid?',
  },
  {
    key: 'areThereSimilarServiceAssets',
    label: 'Are there similar service assets?',
  },
  {
    key: 'confidenceInDamagePrediction',
    label: 'Confidence in Damage Prediction',
  },
  {
    key: 'suitabilityJustification',
    label: 'Justification',
    freeText: true,
  },
]

const LEGACY_SEVERITY_ROWS: ReadonlyArray<LegacyScreeningRow> = [
  {
    key: 'confidenceInPredictingDegradationTypeAndLocation',
    label: 'Confidence in ability to predict type and location of degradation',
  },
  {
    key: 'confidenceInPredictingDegradationTypeAndLocationJustification',
    label:
      'Confidence in ability to predict type and location of degradation: justification',
    freeText: true,
  },
  {
    key: 'previousInspectionEffectiveness',
    label: 'Previous inspection effectiveness',
  },
  {
    key: 'previousInspectionEffectivenessJustification',
    label: 'Previous inspection effectiveness: justification',
    freeText: true,
  },
  {
    key: 'severityAndRateOfDegradation',
    label: 'Severity and rate of degradation',
  },
  {
    key: 'severityAndRateOfDegradationJustification',
    label: 'Severity and rate of degradation: justification',
    freeText: true,
  },
]

export interface NiiScreeningAssessment {
  title: string
  sections: ReadonlyArray<NiiFormSectionBlock>
}

function buildLegacyScreeningSections(
  fieldValues: FieldValues
): ReadonlyArray<NiiFormSectionBlock> {
  // The legacy screening fields aren't backed by section config, so mirror
  // how NiiScreeningFormSection presents them: booleans as Yes/No, enum
  // codes title-cased, justification free text verbatim.
  const read = (row: LegacyScreeningRow): string => {
    const value = unwrapFieldValue(fieldValues[row.key])
    if (!row.freeText && typeof value === 'string' && value !== '') {
      return toTitleCase(value)
    }
    return formatReportFieldValue(value)
  }

  const toRows = (
    definitions: ReadonlyArray<LegacyScreeningRow>
  ): Array<{ label: string; value: string }> =>
    definitions.flatMap((row) => {
      const value = read(row)
      if (value === '—') return []
      return [{ label: row.label, value }]
    })

  const suitabilityRows = toRows(LEGACY_SUITABILITY_ROWS)
  const severityRows = toRows(LEGACY_SEVERITY_ROWS)

  const overrideRows: Array<{ label: string; value: string }> = []
  const override = unwrapFieldValue<boolean>(
    fieldValues.screeningOutcomeOverride
  )
  if (typeof override === 'boolean') {
    overrideRows.push({
      label: 'Screening outcome override',
      value: formatReportFieldValue(override),
    })
  }
  const overrideJustification = unwrapFieldValue<string>(
    fieldValues.screeningOutcomeOverrideJustification
  )
  if (overrideJustification) {
    overrideRows.push({
      label: 'Override reason',
      value: overrideJustification,
    })
  }

  const sections: NiiFormSectionBlock[] = []
  if (suitabilityRows.length > 0) {
    sections.push({ title: 'Suitability', rows: suitabilityRows })
  }
  if (severityRows.length > 0) {
    sections.push({ title: 'NII Screening Logic', rows: severityRows })
  }
  if (overrideRows.length > 0) {
    sections.push({ title: 'Screening Outcome', rows: overrideRows })
  }

  return sections
}

export function buildNiiScreeningContent(
  step: Pick<LibraWorkflowStep, 'name' | 'config'> | undefined,
  ontologyFields: Record<string, OntologyField>,
  fieldValues: FieldValues
): NiiScreeningAssessment | undefined {
  if (!step) return undefined

  const parsed = niiScreeningConfigSchema.safeParse(step.config)
  if (!parsed.success) return undefined

  const sections =
    parsed.data.sections && parsed.data.sections.length > 0
      ? buildNiiFormSectionBlocks(
          parsed.data.sections,
          ontologyFields,
          fieldValues
        )
      : buildLegacyScreeningSections(fieldValues)

  if (sections.length === 0) return undefined

  return {
    title: step.name || 'Screening',
    sections,
  }
}
